"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { fetchWithAuth } from "@/lib/auth";

// ── Types ──────────────────────────────────────────────────────────────────

interface FeedbackQuestion {
    id: string;
    page_category: string;
    prompt: string;
    answer_type: "rating" | "emoji" | "text" | "choice";
    options?: string[] | null;
    sort_order: number;
    is_active: boolean;
}

// Routes where the pill should never appear.
const HIDDEN_PREFIXES = ["/login", "/signup", "/onboarding", "/forgot-password", "/staff"];

const EMOJIS = ["😖", "🙁", "😐", "🙂", "😍"];

// First path segment → page category. "/" → "dashboard"-less → "general".
function categoryFromPath(pathname: string): string {
    const seg = pathname.split("/").filter(Boolean)[0];
    return seg || "general";
}

export default function FeedbackPill() {
    const { user } = useWorkspace();
    const pathname = usePathname();
    const category = useMemo(() => categoryFromPath(pathname), [pathname]);

    const [open, setOpen] = useState(false);
    const [questions, setQuestions] = useState<FeedbackQuestion[]>([]);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [message, setMessage] = useState("");
    const [isBug, setIsBug] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const hidden = !user || HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

    // Fetch questions whenever the form opens for a new category.
    useEffect(() => {
        if (!open || hidden) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchWithAuth(`/api/v1/feedback/questions?page_category=${encodeURIComponent(category)}`);
                if (!res.ok) return;
                const data: FeedbackQuestion[] = await res.json();
                if (!cancelled) setQuestions(data);
            } catch {
                /* silent — feedback is best-effort */
            }
        })();
        return () => { cancelled = true; };
    }, [open, hidden, category]);

    if (hidden) return null;

    const resetForm = () => {
        setAnswers({});
        setMessage("");
        setIsBug(false);
        setSubmitted(false);
    };

    const handleClose = () => {
        sessionStorage.setItem(`fb_nudge_dismissed_${category}`, "1");
        setOpen(false);
        setTimeout(resetForm, 300);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const answerPayload = questions
                .filter((q) => answers[q.id] !== undefined && answers[q.id] !== "")
                .map((q) => ({
                    question_id: q.id,
                    prompt: q.prompt,
                    answer_type: q.answer_type,
                    value: answers[q.id],
                }));
            const res = await fetchWithAuth(`/api/v1/feedback/submissions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    page_category: category,
                    page_path: pathname,
                    is_bug: isBug,
                    message: message.trim() || null,
                    answers: answerPayload,
                }),
            });
            if (res.ok) {
                setSubmitted(true);
                localStorage.setItem("fb_nudge_lastShown", String(Date.now()));
            }
        } catch {
            /* ignore */
        } finally {
            setSubmitting(false);
        }
    };

    const setAnswer = (id: string, value: string) =>
        setAnswers((prev) => ({ ...prev, [id]: value }));

    const hasContent =
        message.trim() !== "" || Object.values(answers).some((v) => v !== "" && v !== undefined);

    return (
        <>
            {/* Collapsed pill */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 rounded-full bg-[#003d45] px-4 py-3 text-white shadow-lg hover:bg-[#007a8a] transition-colors"
                    aria-label="Give feedback"
                >
                    <span className="material-symbols-outlined text-[20px]">feedback</span>
                    <span className="text-sm font-bold hidden sm:inline">Feedback</span>
                </button>
            )}

            {/* Expanded card */}
            {open && (
                <div className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] max-w-sm rounded-2xl border border-[#e2e8f0] bg-white shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between bg-[#003d45] px-5 py-3.5">
                        <div className="flex items-center gap-2 text-white">
                            <span className="material-symbols-outlined text-[20px]">feedback</span>
                            <span className="text-sm font-black uppercase tracking-wider">Share Feedback</span>
                        </div>
                        <button onClick={handleClose} className="text-white/70 hover:text-white" aria-label="Close">
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>
                    </div>

                    {submitted ? (
                        <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                            <span className="material-symbols-outlined text-[40px] text-[#007a8a]">check_circle</span>
                            <p className="text-base font-bold text-[#1a1a1a]">Thank you!</p>
                            <p className="text-sm text-[#666]">Your feedback helps us improve the Hub.</p>
                            <button
                                onClick={handleClose}
                                className="mt-3 rounded-lg bg-[#f0f2f4] px-4 py-2 text-sm font-semibold text-[#333] hover:bg-[#e2e8f0]"
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                        <div className="max-h-[60vh] overflow-y-auto px-5 py-4 space-y-5">
                            {questions.map((q) => (
                                <div key={q.id} className="space-y-2">
                                    <p className="text-sm font-semibold text-[#1a1a1a]">{q.prompt}</p>

                                    {q.answer_type === "rating" && (
                                        <div className="flex gap-1.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <button
                                                    key={n}
                                                    onClick={() => setAnswer(q.id, String(n))}
                                                    className={`flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold transition-colors ${
                                                        answers[q.id] === String(n)
                                                            ? "bg-[#007a8a] text-white"
                                                            : "bg-[#f0f2f4] text-[#666] hover:bg-[#e2e8f0]"
                                                    }`}
                                                >
                                                    {n}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {q.answer_type === "emoji" && (
                                        <div className="flex gap-1.5">
                                            {EMOJIS.map((emo, i) => (
                                                <button
                                                    key={emo}
                                                    onClick={() => setAnswer(q.id, String(i + 1))}
                                                    className={`flex h-10 w-10 items-center justify-center rounded-lg text-xl transition-all ${
                                                        answers[q.id] === String(i + 1)
                                                            ? "bg-[#007a8a]/10 ring-2 ring-[#007a8a] scale-105"
                                                            : "bg-[#f0f2f4] hover:bg-[#e2e8f0]"
                                                    }`}
                                                >
                                                    {emo}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {q.answer_type === "choice" && (
                                        <div className="flex flex-wrap gap-2">
                                            {(q.options || []).map((opt) => (
                                                <button
                                                    key={opt}
                                                    onClick={() => setAnswer(q.id, opt)}
                                                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                                                        answers[q.id] === opt
                                                            ? "bg-[#007a8a] text-white"
                                                            : "bg-[#f0f2f4] text-[#666] hover:bg-[#e2e8f0]"
                                                    }`}
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {q.answer_type === "text" && (
                                        <textarea
                                            value={answers[q.id] || ""}
                                            onChange={(e) => setAnswer(q.id, e.target.value)}
                                            rows={2}
                                            className="w-full resize-none rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm focus:border-[#007a8a] focus:outline-none focus:ring-1 focus:ring-[#007a8a]"
                                            placeholder="Your thoughts…"
                                        />
                                    )}
                                </div>
                            ))}

                            {/* Always-present free text */}
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-[#1a1a1a]">Anything else?</p>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    rows={2}
                                    className="w-full resize-none rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm focus:border-[#007a8a] focus:outline-none focus:ring-1 focus:ring-[#007a8a]"
                                    placeholder="Tell us what's on your mind…"
                                />
                            </div>

                            {/* Bug toggle */}
                            <label className="flex cursor-pointer items-center gap-2.5">
                                <input
                                    type="checkbox"
                                    checked={isBug}
                                    onChange={(e) => setIsBug(e.target.checked)}
                                    className="h-4 w-4 rounded border-[#ccc] text-[#007a8a] focus:ring-[#007a8a]"
                                />
                                <span className="text-sm font-medium text-[#444]">🐞 Report this as a bug</span>
                            </label>

                            <button
                                onClick={handleSubmit}
                                disabled={submitting || !hasContent}
                                className="w-full rounded-xl bg-[#003d45] py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#007a8a] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {submitting ? "Sending…" : "Send Feedback"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
